import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import {
  EnhancedScopeData,
  type FieldValueScopeSource,
  GraphModelShape,
} from '@ardoq/data-model';
import { ReferenceFilterContext } from '@ardoq/filter-helpers';
import { fieldUtils, type FilterColorData } from '@ardoq/scope-data';
import { ComponentFilterData, globalFilters } from './filters';
import { filterCache } from './filterCache';

type WarmFilterCacheHackArgs = {
  scopeData: EnhancedScopeData;
  scopeSource: FieldValueScopeSource;
  graphModel: GraphModelShape;
  referenceFilterContext: ReferenceFilterContext;
};

const toComponentFilterData = (
  component: APIComponentAttributes,
  scopeData: EnhancedScopeData,
  graphModel: GraphModelShape,
  referenceFilterContext: ReferenceFilterContext
): ComponentFilterData => ({
  attributes: component,
  fields: fieldUtils.getFieldsByComponentType(scopeData, {
    modelId: component.model,
    typeId: component.typeId,
  }),
  typeName:
    referenceFilterContext.componentTypeByComponentId[component._id]?.name ??
    '',
  incomingRefCount: graphModel.targetMap.get(component._id)?.length ?? 0,
  outgoingRefCount: graphModel.sourceMap.get(component._id)?.length ?? 0,
});

/**
 * Pre-populates the global `filterCache` for every component and reference in
 * `scopeData`. Replaces the older `conditionalFormattingFilterCacheHack` from
 * `@ardoq/graph` for callers that already build a full `EnhancedScopeData`:
 * bypasses both stub-implementation dispatchers (`graphInterface` and
 * `componentInterface`/`referenceInterface`) and calls `globalFilters` directly.
 *
 * Component filter data is precomputed once and reused as source/target when
 * evaluating references, avoiding the recursive double component-eval the
 * legacy reference wrapper does per call.
 *
 * Caller passes the `referenceFilterContext` (built once from `scopeData` via
 * `buildReferenceFilterContext`) since the same ctx is also threaded into
 * graph traversal downstream.
 */
export const warmFilterCacheHack = ({
  scopeData,
  scopeSource,
  graphModel,
  referenceFilterContext,
}: WarmFilterCacheHackArgs): FilterColorData => {
  if (!filterCache.isWarmedForScope(scopeSource, scopeData)) {
    filterCache.reset();
    filterCache.markWarmedForScope(scopeSource, scopeData);
  }
  const componentFilterDataById = new Map<ArdoqId, ComponentFilterData>();
  for (const component of scopeData.components) {
    const data = toComponentFilterData(
      component,
      scopeData,
      graphModel,
      referenceFilterContext
    );
    componentFilterDataById.set(component._id, data);
    globalFilters.isComponentIncludedInContextByFilter(
      scopeSource,
      data,
      graphModel
    );
  }
  for (const reference of scopeData.references) {
    const source = componentFilterDataById.get(reference.source);
    const target = componentFilterDataById.get(reference.target);
    if (!source || !target) continue;
    globalFilters.isReferenceIncludedInContextByFilter(
      scopeSource,
      {
        attributes: reference,
        fields: fieldUtils.getFieldsByReferenceType(scopeData, {
          modelId: reference.model,
          typeId: String(reference.type),
        }),
        typeName:
          referenceFilterContext.referenceTypeByReferenceId[reference._id]
            ?.name ?? '',
      },
      source,
      target,
      graphModel
    );
  }
  return filterCache.getFilterColorData();
};
